import {SVGElementProxy, SvgView, SvgViewRect} from "./SvgView";
import {padLeft} from "../lib/stringFunctions";
import {ContainerView} from "./Containers";
import {PositionDataAdapter} from "../dashboard/PositionDataSupplier";
import {addSuffixToText} from "../lib/formatTool";
import {ContainerState, ContainerTypeValues} from "../dto/com.rico.sb2.entity.detail";
import {msToDurationMS} from "../lib/timeFunctions";
import {getCurrentTime} from "../dto/com.rico.sb2.service.timeprovider";
import {ContainerIconCache} from "./ContainerIconCache";
import {coalesce} from "../lib/coalesce";

function secToMilli(sec: number) {
	return sec * 1000
}

function milliToSec(milli: number) {
	return Math.round(milli / 1000);
}

export interface PositionViewState {
	readonly data: PositionDataAdapter
	active: boolean
	enabled: boolean
	locked: boolean
	timeLeft: number | null
}

const containerIconCache = new ContainerIconCache();

interface StateModificator {
	update(positionState: number | null): void;
}

class RolloutTranslateY implements StateModificator {
	private readonly _id: number
	private readonly _el: SVGElement
	private readonly _fromY: number
	private readonly _toY: number

	constructor(id: number, el: SVGElement, fromY: number, toY: number) {
		this._id = id;
		this._el = el;
		this._fromY = fromY;
		this._toY = toY;
	}

	update(positionState: number | null) {
		let y = 0;
		switch (positionState || 0) {
			case 1: {
				y = this._toY;
				break
			}
			case 2:
			case 3: {
				y = (this._toY + this._fromY) / 2;
				break
			}
			default: {
				y = this._fromY;
				break
			}
		}
		this._el.setAttribute('transform', `translate(0, ${y})`)
	}
}

export class PositionView extends SvgView implements PositionViewState {
	readonly data: PositionDataAdapter
	active = false
	timeTill: number | null = null
	private _container: ContainerView | null = null

	private iconBoundType: string | null = null
	private iconBoundSvg: SVGElement | null = null

	private clockBoundValue: string | null = null
	private currentString: string | null = null
	private voltageString: string | null = null

	private readonly _containerAnchor: SVGElementProxy | null = null;
	private readonly _stateModificators: StateModificator[] = []

	private _stateKnown: number | null = null

	lineY: number = 0

	/**
	 * Значение enabled, для которого этот отсортирован по глубине.
	 */
	activeByZOrder = true;

	constructor(data: PositionDataAdapter, view: SVGElement | null) {
		super(view)

		const containerAnchor = view?.querySelector(`[data-container-anchor]`) as SVGElement
		if (containerAnchor)
			this._containerAnchor = new SVGElementProxy(containerAnchor)

		if (data.rolloutPosition) {
			view?.querySelectorAll<SVGElement>(`[data-rollout-y-to]`).forEach(svgNode => {
				const yToString = svgNode.getAttribute("data-rollout-y-to");
				if (typeof yToString === 'string' && yToString.length > 0) {
					this._stateModificators.push(new RolloutTranslateY(data.id, svgNode, 0, parseFloat(yToString)));
				}
			});
		}

		this.data = data
		this.bind('id', data.code);
		this.update()
	}

	get container(): ContainerView | null {
		return this._container;
	}

	get containerAnchor(): SvgViewRect {
		return coalesce<SvgViewRect>(this._containerAnchor, this);
	}

	set container(containerView: ContainerView | null) {
		this._container = containerView;
		this.update();
	}

	get enabled(): boolean {
		return this.data.enabled
	}

	get locked(): boolean {
		return this.data.locked;
	}

	set enabled(v: boolean) {
		this.data.enabled = v
	}

	get timeLeft(): number | null {
		if (this.timeTill == null) return null
		return milliToSec(this.timeTill - getCurrentTime())
	}

	set timeLeft(n: number | null) {
		if (n == null) {
			this.timeTill = null
			return
		}
		this.timeTill = new Date(getCurrentTime() + secToMilli(n)).getTime()
	}

	get timeWait(): number | null {
		if (this.container == null) return null

		const containerData = this.container.data;
		if (containerData.state == null) return null;
		switch (containerData.state) {
			case ContainerState.UNLOADING:
			case ContainerState.WAITING:
			case ContainerState.FINISHED:
				if (containerData.programFinishTime == null) return null;
				return Math.round((getCurrentTime() - containerData.programFinishTime) / 1000);
			default:
				return null;
		}
	}

	update() {
		this.toggleClass('enabled', this.enabled)
		this.toggleClass('active', this.active)
		this.toggleClass('locked', this.locked)
		this.toggleClass('OCCUPIED', this._container != null);

		ContainerTypeValues.forEach(enumValue => this.toggleClass(enumValue, this.data.type == enumValue));

		// убираем лишние состояния
		const currentStateClasses = Array.from(this.view?.classList.values() || [])
			.filter(c => /^STATE-\d+$/.test(c))
		const currentStateClass = `STATE-${this.data.state}`;
		if (!(currentStateClass in currentStateClasses)) {
			this.view?.classList.add(currentStateClass)
		}
		currentStateClasses
			.filter(c => c !== currentStateClass)
			.forEach(c => this.view?.classList.remove(c));

		// set type icon
		if (this.iconBoundType != this.data.type) {
			this.iconBoundSvg?.remove();
			this.iconBoundSvg = null;

			this.iconBoundType = this.data.type;
			const iconBoundSvg = containerIconCache.getIcon(this.data.type)
			if (iconBoundSvg) {
				this.iconBoundSvg = iconBoundSvg
				this.bindWith('containerTypeIcon', svg => svg.append(iconBoundSvg))
			}
		}

		this.updateText()

		if (this._stateKnown != this.data.state) {
			this._stateKnown = this.data.state
			this._stateModificators.forEach(mod => mod.update(this._stateKnown))
		}

		if (this.container != null) {
			this.container.toY(this.containerAnchor.y)
		}
	}

	updateText() {
		function formatDurationMilli(milli: number) {
			const {minutes, seconds, forward} = msToDurationMS(milli);
			if (minutes > 999) {
				const hours = Math.floor(minutes / 60);
				return (forward ? '' : '-') + padLeft(Math.min(999, hours).toString(), 2, '0') + 'ч'
			}
			if (minutes > 99) {
				return (forward ? '' : '-') + padLeft(Math.min(999, minutes).toString(), 2, '0') + 'м'
			}
			return (forward ? '' : '-') + padLeft(minutes.toString(), 2, '0') + ':' + padLeft(seconds.toString(), 2, '0')
		}

		let clockBoundValue = ''
		if (this.timeLeft != null) {
			clockBoundValue = formatDurationMilli(this.timeLeft * 1000)
		} else if (this.timeWait != null) {
			clockBoundValue = formatDurationMilli(this.timeWait * 1000)
		}

		this.toggleClass('clock-on', clockBoundValue.length > 0);

		if (clockBoundValue != this.clockBoundValue) {
			this.clockBoundValue = clockBoundValue
			this.bind('clock', clockBoundValue)
		}

		let currentString = addSuffixToText(zeroIfEmpty(this.data.currentString), 'А')
		if (currentString != this.currentString) {
			this.currentString = currentString
			this.bind('current', currentString)
		}

		let voltageString = addSuffixToText(zeroIfEmpty(this.data.voltageString), 'V')
		if (voltageString != this.voltageString) {
			this.voltageString = voltageString
			this.bind('voltage', voltageString)
		}
	}

	tick() {
		if (this.timeTill != null || this.timeWait != null) {
			this.updateText();
		}
	}
}

function zeroIfEmpty(text: string) {
	return text.length == 0 ? '0' : text;
}